import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

interface SelectOptionItem {
  label: string
  value: string
  selected?: boolean
  sourceElement?: BrSelectOptionElement
}

interface BrSelectOptionElement extends HTMLElement {
  label?: string
  value?: string
  selected?: boolean
}

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/select?tab=designer).
 */
@Component({
  tag: 'br-select',
  styleUrl: 'select.scss',
  shadow: true,
  formAssociated: true,
})
export class Select {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrSelectElement

  @AttachInternals() elementInternals: ElementInternals

  private slotElement?: HTMLSlotElement
  private isSyncingOptionElements = false

  /**
   * Rótulo que indica o tipo de informação que deve ser selecionada.
   */
  @Prop({ reflect: true }) label: string

  /**
   * Texto auxiliar exibido antes de uma seleção.
   */
  @Prop({ reflect: true }) placeholder: string = ''

  /**
   * Habilita o modo múltiplo para selecionar várias opções.
   */
  @Prop({ reflect: true }) isMultiple: boolean = false

  /**
   * Controla se o dropdown permanece aberto após selecionar opções.
   * Quando `true`, permite seleção sequencial sem reabrir a lista.
   */
  @Prop({ reflect: true }) keepOpenOnSelect: boolean = false

  /**
   * Define as opções disponíveis no componente de seleção.
   * Se uma string for fornecida, ela será convertida internamente para um array via `JSON.parse`. Caso o formato seja inválido, um erro será registrado e as opções serão definidas como um array vazio.
   * Exemplo de uso:
   * ```typescript
   * options='[{"label": "Sim", "value": "1"}, {"label": "Não", "value": "0"}]'
   * // ou
   * options=[{ label: "Sim", value: "1" }, { label: "Não", value: "0" }]
   * ```
   */
  @Prop({ reflect: true }) options:
    | string
    | {
        label: string
        value: string
        selected?: boolean
      }[] = []

  /**
   * Rótulo para selecionar todas as opções.
   */
  @Prop({ reflect: true }) selectAllLabel: string = 'Selecionar todos'

  /**
   * Rótulo para desmarcar todas as opções.
   */
  @Prop({ reflect: true }) unselectAllLabel: string = 'Desselecionar todos'

  /**
   * Exibe o ícone de busca no campo de entrada.
   */
  @Prop({ reflect: true }) showSearchIcon: boolean = false

  /**
   * Indica se a listagem de itens do select está expandida
   */
  @Prop({ reflect: true }) isOpen: boolean = false

  /**
   * Exibe o rótulo e o controle em linha (ao lado), ao invés de empilhados.
   * Útil para casos contextuais como paginação.
   */
  @Prop({ reflect: true }) isInline: boolean = false

  /**
   * Largura do campo de entrada interno (por exemplo, '88px').
   * Quando definido, sobrescreve a largura padrão de 100% do input.
   */
  @Prop({ reflect: true }) inputWidth?: string

  /**
   * Desabilita a interação com o componente `br-select`.
   * Quando `true`, o input e as opções ficam desabilitados e não respondem a cliques ou teclas.
   */
  @Prop({ reflect: true }) disabled: boolean = false

  /**
   * Propaga para o input interno a remoção da borda quando desfocado.
   */
  @Prop({ reflect: true }) borderless: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-select-${selectId++}`

  // Representa as opções do select disponíveis para renderização.
  // Cada opção contém `label`, `value` e o estado de seleção (`selected`).
  @State() parsedOptions: SelectOptionItem[] = []

  // Opções filtradas de acordo com o termo de busca digitado.
  @State() filteredOptions: SelectOptionItem[] = []

  // Valor exibido no input, refletindo as opções selecionadas.
  @State() inputValue: string = ''

  // Termo digitado no input (modo busca).
  @State() searchTerm: string = ''

  /** Valor selecionado (string para single, string[] para multiple) */
  @Prop({ reflect: false, mutable: true }) value?: string | string[]

  @State() selectedValue?: string | string[]

  // Indica se todas as opções estão selecionadas. Comportamento do checkbox "Selecionar Todos".
  @State() isAllSelected: boolean = false

  // Indica se o estado do checkbox "Selecionar Todos" está indeterminado (algumas, mas não todas, opções selecionadas).
  @State() isIndeterminate: boolean = false

  // Controla se a lista de opções do select está expandida (aberta) ou colapsada (fechada).
  @State() isExpanded: boolean = false

  // Armazena o índice do item atualmente focado, usado para navegação por teclado.
  @State() focusedIndex: number = -1
  @State() optionElements: BrSelectOptionElement[] = []

  /**
   * Evento emitido sempre que houver atualização nos itens selecionados.
   * RENOMEADO PARA valueChange para compatibilidade com ngModel.
   */
  @Event({ bubbles: true, composed: true }) valueChange: EventEmitter

  /** Emite os dados da opção quando ela recebe foco/hover (antes da seleção). */
  @Event({ bubbles: true, composed: true }) optionHover: EventEmitter<{ label: string; value: string }>

  /** Dispara quando o dropdown é aberto. */
  @Event({ bubbles: true, composed: true }) opened: EventEmitter<void>

  /** Dispara quando o dropdown é fechado. */
  @Event({ bubbles: true, composed: true }) closed: EventEmitter<void>

  @Watch('options')
  synchronizeSelect() {
    try {
      const parsedOptions = this.getSourceOptions()

      // Sanitiza as opções (remove opções inválidas, etc.)
      this.parsedOptions = parsedOptions

      // Atualiza lista filtrada considerando o termo atual
      this.filteredOptions = this.getFilteredOptions(this.searchTerm)

      // Atualiza o estado do componente
      this.updateSelectedValueFromParsedOptions()
      this.updateSelectedOptions()
      this.updateSelectedAllState()
      this.emitSelectedOptions()
    } catch (error) {
      console.error('Erro ao processar as opções do select:', error)
      this.parsedOptions = []
    }
  }

  @Watch('value')
  syncValueFromOutside(newVal: string | string[]) {
    // Atualize parsedOptions a partir de newVal
    this.parsedOptions = this.parsedOptions.map((o) => ({
      ...o,
      selected: this.isMultiple ? Array.isArray(newVal) && newVal.includes(o.value) : newVal === o.value,
    }))
    this.selectedValue = newVal
    this.updateSelectedOptions() // mantém inputValue coeso
    this.updateSelectedAllState()
    this.filteredOptions = this.getFilteredOptions(this.searchTerm)
    this.syncOptionElementsFromParsedOptions()
  }

  @Listen('brSelectOptionPropsChange')
  handleSelectOptionPropsChange() {
    if (this.isSyncingOptionElements) return
    if (this.optionElements.length === 0) return
    this.synchronizeSelect()
  }

  componentWillLoad() {
    this.isExpanded = this.isOpen
    this.optionElements = this.getOptionElements()
    this.synchronizeSelect()
  }

  @Watch('isExpanded')
  handleExpandedChange(isExpanded: boolean) {
    if (isExpanded) {
      this.opened.emit()
    } else {
      this.closed.emit()
    }
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  private registerOptionItems = () => {
    this.optionElements = this.getOptionElements()
    this.synchronizeSelect()
  }

  private getOptionElements(): BrSelectOptionElement[] {
    if (this.slotElement !== undefined && this.slotElement !== null) {
      const assignedElements = this.slotElement.assignedElements({ flatten: true }) as HTMLElement[]
      return assignedElements.filter(
        (el) => el.tagName?.toLowerCase() === 'br-select-option'
      ) as BrSelectOptionElement[]
    }

    return Array.from(this.el.querySelectorAll('br-select-option')) as BrSelectOptionElement[]
  }

  private getSourceOptions(): SelectOptionItem[] {
    if (this.optionElements.length > 0) {
      return this.optionElements
        .map((optionElement) => {
          const label = this.getOptionElementLabel(optionElement)
          const value = optionElement.value?.trim() || label

          return {
            label,
            value,
            selected: optionElement.selected === true,
            sourceElement: optionElement,
          }
        })
        .filter((option) => option.label !== '' || option.value !== '')
    }

    return this.getOptionsFromProp()
  }

  private getOptionsFromProp(): SelectOptionItem[] {
    let parsedOptions: SelectOptionItem[] = []

    if (typeof this.options === 'string') {
      try {
        const jsonData = JSON.parse(this.options)
        if (Array.isArray(jsonData)) {
          parsedOptions = jsonData
        } else {
          throw new Error('JSON inválido: deve ser um array')
        }
      } catch (error) {
        console.error('Erro ao converter options para JSON:', error.message)
        parsedOptions = []
      }
    } else if (Array.isArray(this.options)) {
      parsedOptions = this.options
    }

    return parsedOptions
  }

  private getOptionElementLabel(optionElement: BrSelectOptionElement): string {
    const propLabel = optionElement.label?.trim()
    if (propLabel !== undefined && propLabel !== '') return propLabel

    return optionElement.textContent?.trim() || ''
  }

  private updateSelectedValueFromParsedOptions() {
    const selectedValues = this.parsedOptions.filter((option) => option.selected === true).map((option) => option.value)
    const currentValue = typeof this.value === 'string' ? this.value : undefined
    this.selectedValue = this.isMultiple ? selectedValues : (selectedValues[0] ?? currentValue ?? undefined)
    this.value = this.selectedValue
  }

  private syncOptionElementsFromParsedOptions() {
    if (this.optionElements.length === 0) return

    this.isSyncingOptionElements = true

    this.parsedOptions.forEach((option) => {
      if (option.sourceElement !== undefined) {
        if (option.selected === true) {
          option.sourceElement.setAttribute('selected', '')
        } else {
          option.sourceElement.removeAttribute('selected')
        }
      }
    })

    this.isSyncingOptionElements = false
  }

  /**
   * Fecha a lista suspensa (`dropdown`) do `br-select` quando o usuário clica fora do componente.
   * O listener global é adicionado no `componentDidLoad` e removido no `disconnectedCallback` para evitar vazamentos de memória.
   * Se a lista estiver aberta (`this.isExpanded`) e o clique ocorrer fora (`!this.el.contains(target)`), ela é fechada.
   * A reatividade do StencilJS garante a atualização automática da interface.
   * A arrow function (`=>`) mantém o contexto correto de `this` ao ser usada como callback.
   *
   * @param {MouseEvent} event O evento de clique.
   * @returns {void}
   */
  private readonly handleClickOutside = (event: MouseEvent) => {
    // Usa composedPath para considerar nós dentro de Shadow DOM como "dentro" do componente
    const path = (event.composedPath && event.composedPath()) || []
    const clickedInside = path.includes(this.el) || (event.target instanceof Node && this.el.contains(event.target))

    if (this.isExpanded && !clickedInside) {
      this.isExpanded = false
    }
  }

  private handleKeydownOnInput(event: KeyboardEvent) {
    switch (event.key) {
      case 'Tab':
        this.closeSelect()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!this.isExpanded) {
          this.openSelect()
        }
        this.focusItemInDirection('next')
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!this.isExpanded) {
          this.openSelect()
        }
        this.focusItemInDirection('previous')
        break
      case 'Escape':
        this.closeSelect()
        this.resetFocus()
        break
      case 'Enter':
        this.toggleOpen()
        break
      default:
    }
  }

  private handleKeydownOnList(event: KeyboardEvent) {
    event.preventDefault()
    switch (event.key) {
      case 'Tab':
      case 'Escape':
        this.resetFocus()
        break
      case 'ArrowUp':
        this.focusItemInDirection('previous')
        break
      case 'ArrowDown':
        this.focusItemInDirection('next')
        break
      default:
        break
    }
  }

  private handleKeydownOnItem(event: KeyboardEvent, index: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const option = this.filteredOptions[index]
      if (option !== undefined) {
        this.handleSelectionByValue(option.value)
      }
    } else if (event.key === 'Escape') {
      this.closeSelect()
    }
  }

  private handleSelectAllKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggleSelectAll()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.closeSelect()
    }
  }

  private resetFocus() {
    const input = this.el.shadowRoot.querySelector('br-input') as HTMLBrInputElement | null
    input?.focus()
  }

  private focusItemInDirection(direction: 'next' | 'previous') {
    let itemCount = this.parsedOptions.length
    if (this.isMultiple) itemCount += 1

    this.focusedIndex =
      direction === 'next' ? (this.focusedIndex + 1) % itemCount : (this.focusedIndex - 1 + itemCount) % itemCount

    this.focusItem(this.focusedIndex)
  }

  private focusItem(index: number) {
    const items = this.el.shadowRoot.querySelectorAll('.br-item')
    if (items[index]) {
      const item = items[index] as HTMLElement
      item.focus()
      this.emitHoverFromOption(this.filteredOptions[index])
    }
  }

  private emitHoverFromOption(option?: { label: string; value: string }) {
    if (!option) return
    this.optionHover.emit({ label: option.label || option.value, value: option.value })
  }

  private openSelect() {
    this.isExpanded = true
  }

  private closeSelect() {
    this.isExpanded = false
    // Reinicializar o índice focado ao fechar o select
    this.focusedIndex = -1
  }

  private handleSelectionByValue(value: string) {
    const index = this.parsedOptions.findIndex((o) => o.value === value)
    if (index === -1) return

    if (this.isMultiple) {
      this.parsedOptions = this.parsedOptions.map((o, i) => (i === index ? { ...o, selected: !o.selected } : o))
      this.isExpanded = this.keepOpenOnSelect
      this.isFocused = false
      this.searchTerm = ''
    } else {
      this.parsedOptions = this.parsedOptions.map((o, i) => ({
        ...o,
        selected: i === index,
      }))
      this.isExpanded = this.keepOpenOnSelect
      // Se fechou, não estamos mais focados na busca.
      // Isso garante que o valor selecionado seja exibido em vez do termo de busca.
      this.isFocused = false
      this.searchTerm = ''
    }

    this.updateSelectedValueFromParsedOptions()
    this.valueChange.emit(this.selectedValue)

    this.updateSelectedOptions()
    this.updateSelectedAllState()
    this.syncOptionElementsFromParsedOptions()

    // Força a atualização da referência do array para garantir que o Stencil detecte a mudança
    this.filteredOptions = [...this.getFilteredOptions(this.searchTerm)]

    try {
      this.elementInternals.setFormValue(JSON.stringify(this.selectedValue ?? []))
    } catch {}
  }

  private updateSelectedOptions() {
    this.inputValue = this.getStringValue(this.parsedOptions.filter((option) => option.selected))
  }

  private normalizeText(text: string): string {
    return (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  private getFilteredOptions(term: string) {
    if (!term) {
      return this.parsedOptions
    }

    const normalizedTerm = this.normalizeText(term)
    return this.parsedOptions.filter((option) => {
      const normalizedLabel = this.normalizeText(option.label || option.value)
      const normalizedValue = this.normalizeText(option.value)
      return normalizedLabel.includes(normalizedTerm) || normalizedValue.includes(normalizedTerm)
    })
  }

  private getStringValue(options: SelectOptionItem[]) {
    if (options.length > 0) {
      const firstSelected = options[0].label || options[0].value
      if (this.isMultiple) {
        const howManySelected = options.length > 1 ? ` + (${options.length - 1})` : ''
        return `${firstSelected}${howManySelected}`
      } else {
        return firstSelected
      }
    }
    return ''
  }

  private emitSelectedOptions() {
    // A lógica de emissão do evento já está no handleSelection para o ngModel.
    // Esta função pode ser simplificada ou removida se a única finalidade era emitir para ngModel.
    // No entanto, se houver outros consumidores deste evento, você pode mantê-la.
    const selectedValues = this.parsedOptions.filter((option) => option.selected === true).map((option) => option.value) // É importante emitir o 'value' para o ngModel.

    // Já emitimos em handleSelection, mas se quiser ter certeza, pode emitir aqui também.
    // this.valueChange.emit(this.isMultiple ? selectedValues : selectedValues[0] ?? undefined);

    // Atualiza o valor do form associado
    try {
      this.elementInternals.setFormValue(JSON.stringify(this.isMultiple ? selectedValues : (selectedValues[0] ?? null)))
    } catch {
      // Ignora erro em testes onde setFormValue não é implementado
    }
  }

  private updateSelectedAllState() {
    const selectedCount = this.parsedOptions.filter((option) => option.selected).length
    const total = this.parsedOptions.length

    this.isAllSelected = total > 0 && selectedCount === total
    this.isIndeterminate = selectedCount > 0 && selectedCount < total
  }

  private toggleSelectAll() {
    const updatedOptions = this.parsedOptions.map((option) => ({ ...option, selected: !this.isAllSelected }))
    this.parsedOptions = updatedOptions

    this.updateSelectedAllState()
    this.updateSelectedOptions()

    // Atualiza a prop 'value' e emite o evento para ngModel
    this.updateSelectedValueFromParsedOptions()
    this.valueChange.emit(this.selectedValue)
    this.syncOptionElementsFromParsedOptions()

    try {
      this.elementInternals.setFormValue(JSON.stringify(this.selectedValue ?? []))
    } catch {
      // Ignora erro em testes onde setFormValue não é implementado
    }
  }

  // Indica se o input de busca está focado.
  @State() isFocused: boolean = false

  private renderInput() {
    // Se estiver focado, mostramos EXATAMENTE o que está sendo digitado (searchTerm), mesmo que seja vazio.
    // Se não estiver focado, mostramos o termo de busca (se houver) ou o valor selecionado (inputValue).
    const displayValue = this.isFocused ? this.searchTerm : this.searchTerm || this.inputValue

    return (
      <br-input
        exportparts="label"
        value={displayValue}
        label={this.label}
        isInline={this.isInline}
        controlWidth={this.inputWidth}
        borderless={this.borderless}
        placeholder={this.placeholder}
        actionLabel={this.isExpanded ? 'Recolher lista de opções' : 'Exibir lista de opções'}
        actionTabIndex={-1}
        readonly={false}
        disabled={this.disabled}
        onClick={() => {
          if (!this.disabled) this.toggleOpen()
        }}
        onInput={(event) => this.handleSearchInput(event)}
        onFocus={() => this.handleSearchFocus()}
        onBlur={(event) => this.handleSearchBlur(event)}
        onKeyDown={(event) => {
          if (!this.disabled) this.handleKeydownOnInput(event)
        }}
      >
        {this.showSearchIcon && <br-icon slot="icon" icon-name="fa-solid:search" aria-hidden="true"></br-icon>}
        <br-icon
          slot="action"
          icon-name={this.isExpanded ? 'fa-solid:angle-up' : 'fa-solid:angle-down'}
          height="16"
          aria-hidden="true"
        ></br-icon>
      </br-input>
    )
  }

  private renderSelectAllCheckbox() {
    return (
      <div
        class={`br-item highlighted ${this.isAllSelected ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}`}
        data-all="data-all"
        tabindex="-1"
        role="option"
        aria-selected={this.isAllSelected}
        aria-disabled={this.disabled}
        onKeyDown={(event) => {
          if (!this.disabled) this.handleSelectAllKeyDown(event)
        }}
        onClick={() => {
          if (!this.disabled) this.toggleSelectAll()
        }}
      >
        <br-checkbox
          name={`checkbox-${this.customId}`}
          label={this.isAllSelected ? this.unselectAllLabel : this.selectAllLabel}
          checked={this.isAllSelected}
          indeterminate={this.isIndeterminate}
          disabled={this.disabled}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={() => {
            if (!this.disabled) this.toggleSelectAll()
          }}
        ></br-checkbox>
      </div>
    )
  }

  private renderOptions() {
    if (this.filteredOptions.length === 0) {
      return (
        <div class="br-item empty" role="option" aria-selected="false" tabindex={-1}>
          Nenhum resultado encontrado
        </div>
      )
    }

    return this.filteredOptions.map((option, index) => (
      <div
        class={`br-item ${option.selected ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}`}
        key={`${option.label}-${this.customId}`}
        role="option"
        tabindex={this.disabled ? -1 : this.focusedIndex === index ? 0 : -1}
        aria-selected={option.selected}
        aria-disabled={this.disabled}
        data-value={option.value}
        onClick={() => {
          if (!this.disabled) this.handleSelectionByValue(option.value)
        }}
        onKeyDown={(event) => {
          if (!this.disabled) this.handleKeydownOnItem(event, index)
        }}
        onMouseOver={() => this.emitHoverFromOption(option)}
        onPointerOver={() => this.emitHoverFromOption(option)}
        onFocus={() => this.emitHoverFromOption(option)}
      >
        {this.isMultiple ? (
          <br-checkbox
            name={`checkbox-${index}-${this.customId}`}
            label={option.label || option.value}
            checked={option.selected === true}
            disabled={this.disabled}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => {
              if (!this.disabled) this.handleSelectionByValue(option.value)
            }}
          />
        ) : (
          <br-radio
            exportparts="radio-input, radio-label"
            name={`radio-${index}-${this.customId}`}
            label={option.label || option.value}
            checked={option.selected === true}
            disabled={this.disabled}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => {
              if (!this.disabled) this.handleSelectionByValue(option.value)
            }}
          />
        )}
      </div>
    ))
  }

  /**
   * Determina um id para o radio.
   */
  private getComputedId() {
    return this.customId?.length > 0 ? this.customId : `br-select-${this.customId}`
  }

  /**
   * Inverte o valor da prop `isOpen`
   */
  @Method()
  toggleOpen(): Promise<void> {
    this.isExpanded = !this.isExpanded
    return Promise.resolve()
  }

  /**
   * Limpa todas as opções selecionadas.
   */
  @Method()
  clear(): Promise<void> {
    const updatedOptions = this.parsedOptions.map((option) => ({ ...option, selected: false }))
    this.parsedOptions = updatedOptions

    const newValue = this.isMultiple ? [] : undefined
    this.selectedValue = newValue
    this.value = newValue

    this.updateSelectedOptions()
    this.updateSelectedAllState()
    this.valueChange.emit(newValue)
    this.syncOptionElementsFromParsedOptions()

    try {
      this.elementInternals.setFormValue(JSON.stringify(newValue ?? (this.isMultiple ? [] : null)))
    } catch {
      // Ignora erro em testes onde setFormValue não é implementado
    }

    return Promise.resolve()
  }

  /**
   * Retorna o valor selecionado.
   * @returns {Promise<string | string[]>} O valor atual.
   */
  @Method()
  getValue(): Promise<string | string[]> {
    return Promise.resolve(this.value)
  }

  /**
   * Define o valor do componente.
   * @param {string | string[]} newValue O novo valor a ser definido.
   */
  @Method()
  setValue(newValue: string | string[]): Promise<void> {
    this.value = newValue
    // O @Watch('value') cuidará da sincronização
    return Promise.resolve()
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-select': true,
    }
  }

  private handleSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? ''
    this.searchTerm = value
    this.filteredOptions = this.getFilteredOptions(value)
    this.isExpanded = true
  }

  private handleSearchFocus() {
    this.isFocused = true
    this.filteredOptions = this.getFilteredOptions(this.searchTerm)
  }

  private handleSearchBlur(event: FocusEvent) {
    // Se o foco foi movido para dentro do componente (ex: clicou no checkbox/item),
    // não limpamos a busca para manter a lista filtrada estável e permitir o clique.
    const relatedTarget = event.relatedTarget as Node
    if (relatedTarget && (this.el.contains(relatedTarget) || this.el.shadowRoot?.contains(relatedTarget))) {
      return
    }

    this.isFocused = false
    // Ao sair do campo, volta a exibir o resumo das seleções
    this.searchTerm = ''
    this.filteredOptions = this.getFilteredOptions('')
  }

  render() {
    return (
      <Host>
        <div style={{ display: 'none' }} aria-hidden="true">
          <slot
            ref={(el) => {
              this.slotElement = el as HTMLSlotElement
            }}
            onSlotchange={this.registerOptionItems}
          ></slot>
        </div>

        <div class={this.getCssClassMap()} id={this.getComputedId()} aria-disabled={this.disabled}>
          {this.renderInput()}

          {this.isExpanded && (
            <br-list
              tabindex={-1}
              onKeyDown={(event) => this.handleKeydownOnList(event)}
              style={this.inputWidth !== undefined ? { width: this.inputWidth } : undefined}
            >
              {this.isMultiple && this.renderSelectAllCheckbox()}
              {this.renderOptions()}
            </br-list>
          )}
        </div>
      </Host>
    )
  }
}

let selectId = 0
